/**
 * Valor exato vindo do servidor, com o valor derivado do client como piso.
 *
 * ## O contrato (vale para toda contagem por `count()`)
 *
 * Agregação do Firestore é **exata, assíncrona e não reativa** — não existe
 * `onSnapshot` para ela. Três consequências, resolvidas aqui uma vez em vez de
 * em cada tela:
 *
 * 1. **Nunca piscar zero.** Enquanto a resposta não chega, vale o `fallback` —
 *    o número derivado da estante carregada. Pode subcontar, mas é exatamente o
 *    que a tela mostrava antes de existir contagem no servidor: nunca *pior*.
 * 2. **Nunca oscilar.** Toda escrita na estante dispara recontagem. O último
 *    valor exato fica no ar enquanto o próximo está em voo; sem isso, terminar
 *    um livro faria o número cair para depois subir.
 * 3. **Nunca atribuir a resposta ao alvo errado.** O valor guarda a `key` de
 *    quem ele responde. Navegar entre dois perfis públicos mostraria a
 *    contagem exata **da pessoa anterior** — um número confiável na pessoa
 *    errada, que é pior que um provisório na certa.
 *
 * Falha é silenciosa de propósito: o `fallback` já cobre a tela, e um toast de
 * "não consegui contar" não dá ao usuário nada que ele possa fazer. Offline é
 * caminho normal aqui, não exceção — o app roda com `persistentLocalCache`, e
 * **agregação não é servida pelo cache local**.
 */

import { useEffect, useRef, useState } from "react";

export function useServerValue<T>(args: {
  /** Identidade do que está sendo medido (ex.: `${uid}:${ano}`). `null` = não busca. */
  key: string | null;
  /** Valor derivado do client — vale enquanto o exato não chega, e se ele falhar. */
  fallback: T;
  /** Mudou, refaz a busca. Ver `shelfRevision`. */
  revision?: string | number;
  fetch: () => Promise<T>;
}): T {
  const { key, fallback, revision, fetch } = args;
  const [resolved, setResolved] = useState<{ key: string; value: T } | null>(null);

  // `fetch` é recriada a cada render; guardá-la num ref mantém o efeito atado a
  // (key, revision) — que é quando a resposta REALMENTE muda. Sem isso o efeito
  // dispararia a cada render e a contagem viraria uma leitura por frame.
  //
  // ⚠️ A atualização vai num efeito, não no corpo do componente: escrever em ref
  // durante o render é inseguro com renderização concorrente (o React pode
  // descartar o render) e a regra `react-hooks/refs` recusa. Este efeito é
  // declarado ANTES do de busca de propósito — efeitos disparam na ordem de
  // declaração, então quando a busca roda o ref já tem a `fetch` do render
  // corrente.
  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  });

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    fetchRef
      .current()
      .then((value) => {
        if (!cancelled) setResolved({ key, value });
      })
      .catch(() => {
        /* ver §contrato: o fallback cobre. */
      });
    return () => {
      cancelled = true;
    };
  }, [key, revision]);

  return resolved?.key === key ? resolved.value : fallback;
}
